<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Avatar from 'next/avatar/Avatar.vue';
import Badge from 'dashboard/components-next/badge/Badge.vue';

const props = defineProps({
  tramite: { type: Object, required: true },
});

defineEmits(['open']);

const { t } = useI18n();

// Solid status pills on Radix `n-` scales → dark-mode safe.
const STATUS_PILL = {
  draft: 'bg-n-slate-9 text-white',
  submitted: 'bg-n-blue-9 text-white',
  in_review: 'bg-n-amber-9 text-white',
  awaiting_citizen: 'bg-n-amber-9 text-white',
  approved: 'bg-n-teal-9 text-white',
  rejected: 'bg-n-ruby-9 text-white',
  completed: 'bg-n-teal-9 text-white',
  cancelled: 'bg-n-slate-8 text-white',
};

const PRIORITY_FLAG = {
  urgent: 'text-n-ruby-9',
  high: 'text-n-amber-9',
  medium: 'text-n-blue-9',
  low: 'text-n-slate-9',
};

const statusPill = computed(
  () => STATUS_PILL[props.tramite.status] || STATUS_PILL.draft
);

const statusLabel = computed(() =>
  t(`TRAMITES.STATUS.${(props.tramite.status || 'draft').toUpperCase()}`)
);

const typeName = computed(() => props.tramite.tramite_type?.name || '—');
const stageName = computed(() => props.tramite.current_stage?.name || '—');
const folio = computed(
  () => props.tramite.folio || `${t('TRAMITES.NUMBER_PREFIX')}${props.tramite.display_id}`
);
</script>

<template>
  <div
    class="flex items-center gap-3 px-6 py-2.5 cursor-pointer hover:bg-n-alpha-1"
    @click="$emit('open', tramite)"
  >
    <span class="font-mono text-xs shrink-0 w-20 truncate text-n-slate-9">
      {{ folio }}
    </span>

    <span class="flex-1 min-w-0 text-sm truncate text-n-slate-12">
      {{ tramite.title }}
    </span>

    <span class="hidden text-xs truncate sm:block w-32 shrink-0 text-n-slate-10">
      {{ typeName }}
    </span>

    <span
      class="inline-flex items-center px-2 h-6 text-xs font-medium rounded-md shrink-0 w-28 justify-center"
      :class="statusPill"
    >
      <span class="truncate">{{ statusLabel }}</span>
    </span>

    <span class="hidden text-xs truncate md:block w-32 shrink-0 text-n-slate-10">
      {{ stageName }}
    </span>

    <Badge v-if="tramite.overdue" color="red" size="small">
      {{ t('TRAMITES.BADGES.OVERDUE') }}
    </Badge>

    <span class="flex justify-center w-5 shrink-0">
      <span
        v-if="tramite.priority && tramite.priority !== 'medium'"
        v-tooltip.top="t(`TRAMITES.PRIORITY.${tramite.priority.toUpperCase()}`)"
        class="i-lucide-flag"
        :class="PRIORITY_FLAG[tramite.priority]"
      />
    </span>

    <span class="flex justify-center w-6 shrink-0">
      <Avatar v-if="tramite.assignee" :name="tramite.assignee.name" :size="22" />
    </span>
  </div>
</template>
